import { useEffect, useState } from 'react';
import { Badge, Breadcrumb, Button, Card, Col, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Private_Routes } from '../../constants';
import ErrorMessage from '../../common/filter-table/ErrorMessage';
import Loading from '../../common/filter-table/Loading';
import { fetchAuditLogById } from './api/auditLogs.api';
import { AuditDiffViewer } from './components/AuditDiffViewer';
import { RelatedAuditEntitiesPanel } from './components/RelatedAuditEntitiesPanel';
import { RequireAuditModuleAccess } from './RequireAuditModuleAccess';
import { AuditLogDetailsDto } from './models/auditLog.model';
import { formatAuditDate, getOperationKind } from './utils/auditLogHelpers';

function AuditLogDetailsInner() {
    const { auditLogId } = useParams();
    const location = useLocation();
    const state = location.state as
        | { listSearchSuffix?: string; entityName?: string; primaryKey?: number }
        | undefined;

    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState<AuditLogDetailsDto | null>(null);
    const [error, setError] = useState<{ title: string; message: string } | null>(null);

    const backHref = (() => {
        if (state?.listSearchSuffix != null && state.listSearchSuffix !== '') {
            return `${Private_Routes.AUDIT_LOGS.index}${state.listSearchSuffix}`;
        }
        if (state?.entityName != null && state?.primaryKey != null) {
            return `${Private_Routes.AUDIT_LOGS.index}?entityName=${encodeURIComponent(
                state.entityName
            )}&primaryKey=${state.primaryKey}`;
        }
        return Private_Routes.AUDIT_LOGS.index;
    })();

    useEffect(() => {
        const id = Number(auditLogId);
        if (Number.isNaN(id)) {
            setLoading(false);
            setError({
                title: 'Solicitud inválida',
                message: 'El identificador de auditoría no es válido.',
            });
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        void (async () => {
            const res = await fetchAuditLogById(id);
            if (cancelled) {
                return;
            }
            setLoading(false);
            if (res.ok) {
                setDetail(res.data);
                return;
            }
            if (res.status === 404) {
                setError({
                    title: 'No encontrado',
                    message:
                        'No existe un registro de auditoría con ese id o fue eliminado del sistema.',
                });
                return;
            }
            if (res.status === 403) {
                setError({
                    title: 'Acceso denegado',
                    message:
                        res.message ||
                        'No tiene permisos para ver este detalle de auditoría.',
                });
                return;
            }
            setError({
                title: 'Error',
                message: res.message || 'No se pudo cargar el detalle.',
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [auditLogId]);

    const main = detail?.entities?.[0];

    const opKind = detail ? getOperationKind(detail.operation) : 'unknown';
    const opBadge =
        opKind === 'create'
            ? 'success'
            : opKind === 'update'
              ? 'primary'
              : opKind === 'delete'
                ? 'danger'
                : 'secondary';

    return (
        <div className='main-content'>
            <div className='page-content'>
                <div className='container-fluid'>
                    <Helmet>
                        <title>Detalle de auditoría</title>
                    </Helmet>

                    <Row className='py-0'>
                        <Col sm={12} className='p-3 py-0'>
                            <Breadcrumb>
                                <Breadcrumb.Item href={Private_Routes.HOME}>Inicio</Breadcrumb.Item>
                                <Breadcrumb.Item href={Private_Routes.AUDIT_LOGS.index}>
                                    Auditoría
                                </Breadcrumb.Item>
                                <Breadcrumb.Item active>Detalle</Breadcrumb.Item>
                            </Breadcrumb>

                            <div className='mb-3'>
                                <Link to={backHref} className='btn btn-soft-secondary btn-sm'>
                                    <i className='ri-arrow-left-line me-1' />
                                    Volver al listado
                                </Link>
                            </div>

                            {loading && <Loading hasSkeleton={true} skeletonType='table' />}

                            {!loading && error && (
                                <ErrorMessage title={error.title} errorDescription={error.message} />
                            )}

                            {!loading && !error && detail && (
                                <>
                                    <Card className='mb-3'>
                                        <Card.Header>
                                            <h5 className='mb-0'>Evento de auditoría</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row className='g-2'>
                                                <Col md={6}>
                                                    <div className='text-muted small'>Entidad</div>
                                                    <div>{detail.parsedEntityName || detail.entityName}</div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className='text-muted small'>Registro</div>
                                                    <div>
                                                        {detail.primaryKeyDescription || detail.primaryKey}
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className='text-muted small'>Operación</div>
                                                    <div>
                                                        <Badge bg={opBadge}>{detail.operationDescription}</Badge>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className='text-muted small'>Usuario</div>
                                                    <div>{detail.userFullName || '—'}</div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className='text-muted small'>Empleado</div>
                                                    <div>{detail.employeeFullName || '—'}</div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className='text-muted small'>Fecha</div>
                                                    <div>{formatAuditDate(detail.date)}</div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className='text-muted small'>Correlación</div>
                                                    <div className='text-break small'>{detail.correlationId}</div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    {main && (
                                        <Card className='mb-3'>
                                            <Card.Header>
                                                <span className='fw-medium'>Valores (principal)</span>
                                                <span className='text-muted small ms-2'>
                                                    {main.entityName} · PK {main.primaryKey}
                                                </span>
                                            </Card.Header>
                                            <Card.Body className='p-0'>
                                                <div className='p-2'>
                                                    <AuditDiffViewer fields={main.fields} />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    )}

                                    {detail.entities && detail.entities.length > 1 && (
                                        <RelatedAuditEntitiesPanel entities={detail.entities} />
                                    )}

                                    <div className='mt-3'>
                                        <Link to={backHref}>
                                            <Button variant='primary'>
                                                <i className='ri-arrow-left-line me-1' />
                                                Volver al listado
                                            </Button>
                                        </Link>
                                    </div>
                                </>
                            )}
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
}

export default function AuditLogDetailsPage() {
    return (
        <RequireAuditModuleAccess>
            <AuditLogDetailsInner />
        </RequireAuditModuleAccess>
    );
}
